/* =============================================================
   Cart Drawer – herschreven met defensieve layout-bescherming
   -------------------------------------------------------------
   Behoudt alle originele functionaliteit van Dawn, plus:
   • iOS-veilige body-scroll-lock (overflow:hidden faalt op iOS)
   • Normalisatie van <img> attributen om CSS-overrides te
     voorkomen (apps zetten soms inline width/height/style)
   • MutationObserver: blijft layout-fixes doorvoeren als cart
     opnieuw rendert door een upsell-app
   • ResizeObserver: hertekent op orientation-change
   • data-cart-drawer-ready hook voor CSS targeting
   • Veilige cleanup bij sluiten
   ============================================================= */

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this._scrollY = 0;
    this._mutationObserver = null;
    this._resizeObserver = null;

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();

    // Eerste keer layout-bescherming toepassen op bestaande inhoud
    this.applyLayoutDefenses();

    // Observer die layout-defenses opnieuw toepast bij herrender
    this.startMutationObserver();
  }

  disconnectedCallback() {
    if (this._mutationObserver) this._mutationObserver.disconnect();
    if (this._resizeObserver) this._resizeObserver.disconnect();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);

    // Layout-defenses opnieuw aftrappen voor we openen
    this.applyLayoutDefenses();

    // Animation-trigger met timeout (zoals origineel Dawn)
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    // iOS-veilige body-scroll-lock
    this.lockBodyScroll();

    // ResizeObserver pas activeren als drawer open is
    this.startResizeObserver();

    this.setAttribute('data-cart-drawer-ready', 'true');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);

    // Body-scroll-lock weer opheffen
    this.unlockBodyScroll();

    if (this._resizeObserver) {
      this._resizeObserver.disconnect();
      this._resizeObserver = null;
    }

    this.removeAttribute('data-cart-drawer-ready');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling && cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute(
        'aria-expanded',
        !event.currentTarget.closest('details').hasAttribute('open')
      );
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    const inner = this.querySelector('.drawer__inner');
    if (inner && inner.classList.contains('is-empty')) {
      inner.classList.remove('is-empty');
    }
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      const overlay = this.querySelector('#CartDrawer-Overlay');
      if (overlay) overlay.addEventListener('click', this.close.bind(this));

      // Layout-defenses opnieuw toepassen na re-render
      this.applyLayoutDefenses();

      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const parsed = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return parsed ? parsed.innerHTML : '';
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  /* -----------------------------------------------------------
     LAYOUT-DEFENSES
     -----------------------------------------------------------
     Verwijdert/normaliseert inline attributen op <img> binnen
     cart-items zodat onze CSS niet wordt overschreven door
     inline styles van Shopify of apps. Voegt ook een hulp-class
     toe zodra een afbeelding daadwerkelijk geladen is, voor
     soepelere visuele transities.
     ----------------------------------------------------------- */
  applyLayoutDefenses() {
    const items = this.querySelectorAll('.cart-item, [class*="cart-item"]');
    items.forEach((item) => {
      // Markeer voor CSS-targeting
      item.setAttribute('data-cart-item-normalized', 'true');

      // Normaliseer alle <img>-elementen binnen het cart-item
      item.querySelectorAll('img').forEach((img) => {
        // Inline style die de breedte forceert kan onze CSS breken
        if (img.style && img.style.width) img.style.removeProperty('width');
        if (img.style && img.style.height) img.style.removeProperty('height');
        if (img.style && img.style.maxWidth) img.style.removeProperty('max-width');
        if (img.style && img.style.position) img.style.removeProperty('position');
        if (img.style && img.style.float) img.style.removeProperty('float');

        // HTML width/height attributen worden door browsers behandeld
        // als presentation hints — die laten we staan voor lazy-load,
        // maar zorgen dat ze in elk geval niet absurd zijn.
        const w = parseInt(img.getAttribute('width'), 10);
        if (w && w > 600) img.setAttribute('width', '600');

        // Markeer wanneer geladen
        if (img.complete && img.naturalHeight !== 0) {
          img.setAttribute('data-cart-img-loaded', 'true');
        } else {
          img.addEventListener(
            'load',
            () => img.setAttribute('data-cart-img-loaded', 'true'),
            { once: true }
          );
          img.addEventListener(
            'error',
            () => img.setAttribute('data-cart-img-error', 'true'),
            { once: true }
          );
        }
      });

      // Normaliseer media-container
      const media = item.querySelector('.cart-item__media, [class*="cart-item__media"]');
      if (media && media.style) {
        if (media.style.width) media.style.removeProperty('width');
        if (media.style.position) media.style.removeProperty('position');
      }
    });
  }

  /* -----------------------------------------------------------
     MUTATION OBSERVER
     Pakt het geval op waar een upsell-app de cart-drawer-inhoud
     vervangt buiten renderContents() om.
     ----------------------------------------------------------- */
  startMutationObserver() {
    if (typeof MutationObserver === 'undefined') return;
    if (this._mutationObserver) return;

    this._mutationObserver = new MutationObserver((mutations) => {
      let shouldReapply = false;
      for (const m of mutations) {
        if (m.type === 'childList' && m.addedNodes.length > 0) {
          for (const node of m.addedNodes) {
            if (node.nodeType !== 1) continue;
            if (
              node.matches?.('.cart-item, [class*="cart-item"]') ||
              node.querySelector?.('.cart-item, [class*="cart-item"]')
            ) {
              shouldReapply = true;
              break;
            }
          }
        }
        if (shouldReapply) break;
      }
      if (shouldReapply) {
        // Debounce zodat we niet bij elke mutation opnieuw werken
        if (this._reapplyTimer) clearTimeout(this._reapplyTimer);
        this._reapplyTimer = setTimeout(() => this.applyLayoutDefenses(), 50);
      }
    });

    this._mutationObserver.observe(this, {
      childList: true,
      subtree: true,
    });
  }

  /* -----------------------------------------------------------
     RESIZE OBSERVER
     Forceert een nieuwe layout-pass bij viewport- of
     orientation-wijzigingen. CSS doet het meeste werk; dit is
     een vangnet voor browsers die niet vlekkeloos herrekenen.
     ----------------------------------------------------------- */
  startResizeObserver() {
    if (typeof ResizeObserver === 'undefined') return;
    if (this._resizeObserver) return;

    const inner = this.querySelector('.drawer__inner');
    if (!inner) return;

    this._resizeObserver = new ResizeObserver(() => {
      this.setAttribute('data-viewport-width', window.innerWidth);
    });
    this._resizeObserver.observe(inner);

    // Initial value zetten
    this.setAttribute('data-viewport-width', window.innerWidth);
  }

  /* -----------------------------------------------------------
     iOS-VEILIGE BODY-SCROLL-LOCK
     Op iOS Safari werkt overflow:hidden niet voor body. We
     bewaren de scroll-positie, zetten body op position:fixed,
     en herstellen alles bij close.
     ----------------------------------------------------------- */
  lockBodyScroll() {
    document.body.classList.add('overflow-hidden');

    // iOS-detectie via touch-support + macachtig user agent of iPad
    const isIOS =
      /iPad|iPhone|iPod/.test(navigator.userAgent) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

    if (isIOS) {
      this._scrollY = window.scrollY || window.pageYOffset || 0;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${this._scrollY}px`;
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.width = '100%';
    }
  }

  unlockBodyScroll() {
    document.body.classList.remove('overflow-hidden');

    const isIOS =
      /iPad|iPhone|iPod/.test(navigator.userAgent) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

    if (isIOS) {
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.width = '';
      // Scroll-positie herstellen
      window.scrollTo(0, this._scrollY || 0);
    }
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }

  // Na elke cart-update opnieuw layout-defenses toepassen
  onCartUpdate() {
    if (super.onCartUpdate) super.onCartUpdate();
    const drawer = this.closest('cart-drawer');
    if (drawer && typeof drawer.applyLayoutDefenses === 'function') {
      requestAnimationFrame(() => drawer.applyLayoutDefenses());
    }
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);